// src/store/modules/notifications.ts
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import { notificationApi } from '@/services/api';

export interface NotificationItem {
  id: string;
  type:
    | 'new_message'
    | 'user_follow'
    | 'product_follow'
    | 'product_expired'
    | 'new_rating';
  read: boolean;
  createdAt: Date;
  message?: {
    id: string;
    content: string;
  };
  sender?: {
    id: number;
    username: string;
  };
  product?: {
    id: number;
    name: string;
    slug: string;
  };
  follower?: {
    id: number;
    username: string;
  };
  rater?: {
    id: number;
    username: string;
  };
  rating?: {
    type: string;
    isLike: boolean;
    comment?: string;
  };
  timestamp: string;

  data?: {
    sender?: {
      id: string;
      username: string;
    };
    message?: {
      id: string;
      content: string;
    };
    product?: {
      id: string;
      name: string;
      slug: string;
      userId?: string;
    };
    follower?: {
      id: string;
      username: string;
    };
    followed?: {
      id: string;
      username: string;
    };
    rater?: {
      id: string;
      username: string;
    };
    rating?: {
      type: string;
      isLike: boolean;
      comment?: string;
    };
    [key: string]: any; // Za dodatne properties
  };
}

export interface NotificationBadge {
  messages: number;
  follows: number;
  products: number;
  ratings: number;
  total: number;
}

export interface NotificationsState {
  notifications: NotificationItem[];
  badges: NotificationBadge;
  isConnected: boolean;
  connectionError: string | null;
  lastNotificationId: string | null;
}

const state: NotificationsState = {
  notifications: [],
  badges: {
    messages: 0,
    follows: 0,
    products: 0,
    ratings: 0,
    total: 0,
  },
  isConnected: false,
  connectionError: null,
  lastNotificationId: null,
};

const getters = {
  unreadNotifications: (state: NotificationsState) =>
    state.notifications.filter((n) => !n.read),

  unreadCount: (state: NotificationsState, getters: any) =>
    getters.unreadNotifications.length,

  hasUnreadMessages: (state: NotificationsState) => state.badges.messages > 0,

  hasUnreadFollows: (state: NotificationsState) => state.badges.follows > 0,

  hasUnreadProducts: (state: NotificationsState) => state.badges.products > 0,

  hasUnreadRatings: (state: NotificationsState) => state.badges.ratings > 0,

  recentNotifications: (state: NotificationsState) =>
    state.notifications.slice(0, 10),

  connectionStatus: (state: NotificationsState) => ({
    isConnected: state.isConnected,
    error: state.connectionError,
  }),

  totalBadgeCount: (state: NotificationsState) => state.badges.total,
};

const mutations = {
  ADD_NOTIFICATION(state: NotificationsState, notification: NotificationItem) {
    console.log('[Notifications] Adding notification:', notification);

    // Generate unique ID if not provided
    if (!notification.id) {
      notification.id = `${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
    }

    // Set timestamp if not provided
    if (!notification.createdAt) {
      notification.createdAt = new Date(notification.timestamp || new Date());
    }

    // Ensure notification is unread by default
    if (notification.read === undefined) {
      notification.read = false;
    }

    // Add to beginning of array
    state.notifications.unshift(notification);

    // Update badges only if notification is unread
    if (!notification.read) {
      switch (notification.type) {
        case 'new_message':
          state.badges.messages++;
          break;
        case 'user_follow':
        case 'product_follow':
          state.badges.follows++;
          break;
        case 'product_expired':
          state.badges.products++;
          break;
        case 'new_rating':
          state.badges.ratings++;
          break;
      }

      // Recalculate total
      state.badges.total =
        state.badges.messages +
        state.badges.follows +
        state.badges.products +
        state.badges.ratings;
    }

    // Keep only last 100 notifications
    if (state.notifications.length > 100) {
      state.notifications = state.notifications.slice(0, 100);
    }

    // Track last notification
    state.lastNotificationId = notification.id;
    console.log('[Notifications] Badge counts updated:', state.badges);
  },

  MARK_AS_READ(state: NotificationsState, notificationId: string) {
    console.log('[Notifications] Marking as read:', notificationId);

    const notification = state.notifications.find(
      (n) => n.id === notificationId
    );
    if (notification && !notification.read) {
      notification.read = true;

      // Update badges
      switch (notification.type) {
        case 'new_message':
          state.badges.messages = Math.max(0, state.badges.messages - 1);
          break;
        case 'user_follow':
        case 'product_follow':
          state.badges.follows = Math.max(0, state.badges.follows - 1);
          break;
        case 'product_expired':
          state.badges.products = Math.max(0, state.badges.products - 1);
          break;
        case 'new_rating':
          state.badges.ratings = Math.max(0, state.badges.ratings - 1);
          break;
      }

      // Recalculate total
      state.badges.total =
        state.badges.messages +
        state.badges.follows +
        state.badges.products +
        state.badges.ratings;

      console.log(
        '[Notifications] Badge counts after mark as read:',
        state.badges
      );
    }
  },

  MARK_ALL_AS_READ(state: NotificationsState) {
    console.log('[Notifications] Marking all as read');

    state.notifications.forEach((n) => {
      n.read = true;
    });

    // Reset all badges
    state.badges = {
      messages: 0,
      follows: 0,
      products: 0,
      ratings: 0,
      total: 0,
    };
    console.log('[Notifications] All badges reset');
  },

  REMOVE_NOTIFICATION(state: NotificationsState, notificationId: string) {
    console.log('[Notifications] Removing notification:', notificationId);

    const index = state.notifications.findIndex((n) => n.id === notificationId);
    if (index > -1) {
      const notification = state.notifications[index];

      // Update badges if notification was unread
      if (!notification.read) {
        switch (notification.type) {
          case 'new_message':
            state.badges.messages = Math.max(0, state.badges.messages - 1);
            break;
          case 'user_follow':
          case 'product_follow':
            state.badges.follows = Math.max(0, state.badges.follows - 1);
            break;
          case 'product_expired':
            state.badges.products = Math.max(0, state.badges.products - 1);
            break;
          case 'new_rating':
            state.badges.ratings = Math.max(0, state.badges.ratings - 1);
            break;
        }

        // Recalculate total
        state.badges.total =
          state.badges.messages +
          state.badges.follows +
          state.badges.products +
          state.badges.ratings;
      }

      state.notifications.splice(index, 1);
      console.log('[Notifications] Badge counts after removal:', state.badges);
    }
  },

  CLEAR_ALL_NOTIFICATIONS(state: NotificationsState) {
    console.log('[Notifications] Clearing all notifications');

    state.notifications = [];
    state.badges = {
      messages: 0,
      follows: 0,
      products: 0,
      ratings: 0,
      total: 0,
    };
    state.lastNotificationId = null;
  },

  SET_CONNECTION_STATUS(
    state: NotificationsState,
    { isConnected, error }: { isConnected: boolean; error?: string | null }
  ) {
    console.log('[Notifications] Connection status changed:', {
      isConnected,
      error,
    });

    state.isConnected = isConnected;
    state.connectionError = error || null;
  },

  // Load notifications from localStorage or API
  LOAD_NOTIFICATIONS(
    state: NotificationsState,
    notifications: NotificationItem[]
  ) {
    console.log('[Notifications] Loading notifications:', notifications.length);

    state.notifications = notifications;

    // Recalculate badges
    const badges = {
      messages: 0,
      follows: 0,
      products: 0,
      ratings: 0,
      total: 0,
    };

    notifications.forEach((n) => {
      if (!n.read) {
        switch (n.type) {
          case 'new_message':
            badges.messages++;
            break;
          case 'user_follow':
          case 'product_follow':
            badges.follows++;
            break;
          case 'product_expired':
            badges.products++;
            break;
          case 'new_rating':
            badges.ratings++;
            break;
        }
      }
    });

    badges.total =
      badges.messages + badges.follows + badges.products + badges.ratings;
    state.badges = badges;

    console.log('[Notifications] Badges recalculated:', state.badges);
  },
};

const actions = {
  addNotification({ commit }: any, notificationData: any) {
    console.log(
      '[Notifications Action] Adding notification:',
      notificationData
    );

    const notification: NotificationItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: notificationData.type,
      read: false,
      createdAt: new Date(notificationData.timestamp || new Date()),
      timestamp: notificationData.timestamp || new Date().toISOString(),
      ...notificationData,
    };

    commit('ADD_NOTIFICATION', notification);
  },

  markAsRead({ commit }: any, notificationId: string) {
    commit('MARK_AS_READ', notificationId);
  },

  markAllAsRead({ commit }: any) {
    commit('MARK_ALL_AS_READ');
  },

  removeNotification({ commit }: any, notificationId: string) {
    commit('REMOVE_NOTIFICATION', notificationId);
  },

  clearAllNotifications({ commit }: any) {
    commit('CLEAR_ALL_NOTIFICATIONS');
  },

  setConnectionStatus(
    { commit }: any,
    status: { isConnected: boolean; error?: string | null }
  ) {
    commit('SET_CONNECTION_STATUS', status);
  },

  // ✅ NOVE ACTIONS ZA API POZIVE:

  // Učitaj notifikacije iz API-ja/baze
  async loadNotifications({ commit }: any) {
    try {
      console.log('[Notifications Store] Loading notifications from API...');

      // Import notificationApi ovde da izbegneš circular dependency
      const { notificationApi } = await import('@/services/api');

      // Pozovi API da učitaš neisporučene notifikacije
      const response = await notificationApi.getUndeliveredNotifications();

      if (response.status === 'success' && response.data) {
        const notifications = response.data.map((n: any) => ({
          ...n,
          createdAt: new Date(n.createdAt),
          timestamp: n.createdAt || n.timestamp,
        }));

        console.log(
          `[Notifications Store] Loaded ${notifications.length} undelivered notifications`
        );
        commit('LOAD_NOTIFICATIONS', notifications);

        return notifications;
      } else {
        console.log(
          '[Notifications Store] No undelivered notifications or invalid response format'
        );
        return [];
      }
    } catch (error) {
      console.error(
        '[Notifications Store] Error loading notifications from API:',
        error
      );

      // Fallback na localStorage ako API ne radi
      try {
        const stored = localStorage.getItem('notifications');
        if (stored) {
          const notifications = JSON.parse(stored).map((n: any) => ({
            ...n,
            createdAt: new Date(n.createdAt),
          }));
          commit('LOAD_NOTIFICATIONS', notifications);
          console.log(
            `[Notifications Store] Loaded ${notifications.length} notifications from localStorage as fallback`
          );
          return notifications;
        }
      } catch (localStorageError) {
        console.error(
          '[Notifications Store] Error loading from localStorage:',
          localStorageError
        );
      }

      return [];
    }
  },

  // Označi kao pročitano preko API-ja
  async markAsReadAPI({ commit }: any, notificationId: string) {
    try {
      console.log(
        `[Notifications Store] Marking notification ${notificationId} as read via API`
      );

      // Prvo označi lokalno
      commit('MARK_AS_READ', notificationId);

      // Zatim pošalji na API
      const { notificationApi } = await import('@/services/api');
      await notificationApi.markAsRead(notificationId);

      console.log(
        `[Notifications Store] Notification ${notificationId} marked as read successfully`
      );
    } catch (error) {
      console.error(
        '[Notifications Store] Error marking notification as read:',
        error
      );
      // Možda da se vrati lokalna promena ako API ne uspe?
      throw error;
    }
  },

  // Označi sve kao pročitano preko API-ja
  async markAllAsReadAPI({ commit }: any) {
    try {
      console.log(
        '[Notifications Store] Marking all notifications as read via API'
      );

      // Prvo označi lokalno
      commit('MARK_ALL_AS_READ');

      // Zatim pošalji na API
      const { notificationApi } = await import('@/services/api');
      await notificationApi.markAllAsRead();

      console.log(
        '[Notifications Store] All notifications marked as read successfully'
      );
    } catch (error) {
      console.error(
        '[Notifications Store] Error marking all notifications as read:',
        error
      );
      throw error;
    }
  },

  // Save notifications to localStorage
  saveNotifications({ state }: any) {
    try {
      localStorage.setItem(
        'notifications',
        JSON.stringify(state.notifications)
      );
    } catch (error) {
      console.error('[Notifications] Error saving notifications:', error);
    }
  },
};

const notificationsModule: Module<NotificationsState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default notificationsModule;
